import React, { useMemo } from "react";
import EChart from "./EChart";
import { ECHART_TOOLTIP, ECHART_TOOLBOX, ECHART_COLORS } from "../../Utilities/echartsTheme";
import { formatNumber, formatRawNumber } from "../../Utilities/numbers";

// Vertical bar chart of orders by hour of day (0-23), with the peak hour highlighted.
// data: [{ hour: 0..23, order_count: N, revenue: R }, ...]
const EChartsOrdersByHour = ({ data }) => {
  const hasData = Array.isArray(data) && data.length > 0;

  const { hours, counts, revenues, peakHour, peakCount } = useMemo(() => {
    const byHour = {};
    let totalMax = 0;
    let totalMaxHour = 0;
    (data || []).forEach((row) => {
      const h = Number(row.hour);
      if (!Number.isInteger(h) || h < 0 || h > 23) return;
      byHour[h] = {
        count: Number(row.order_count) || 0,
        revenue: Number(row.revenue) || 0
      };
      if (byHour[h].count > totalMax) {
        totalMax = byHour[h].count;
        totalMaxHour = h;
      }
    });

    const hourList = [];
    const countList = [];
    const revenueList = [];
    for (let h = 0; h < 24; h++) {
      hourList.push(`${String(h).padStart(2, "0")}:00`);
      countList.push(byHour[h]?.count || 0);
      revenueList.push(byHour[h]?.revenue || 0);
    }
    return {
      hours: hourList,
      counts: countList,
      revenues: revenueList,
      peakHour: totalMaxHour,
      peakCount: totalMax
    };
  }, [data]);

  const option = useMemo(() => {
    if (!hasData) return {};

    const peakLabel = `${String(peakHour).padStart(2, "0")}:00`;

    return {
      tooltip: {
        ...ECHART_TOOLTIP,
        trigger: "axis",
        axisPointer: { type: "shadow" },
        formatter: (params) => {
          const p = params[0];
          const idx = p.dataIndex;
          const revenue = revenues[idx] || 0;
          return `<div style="font-weight:600;margin-bottom:4px">${p.axisValue}</div>
            <div style="display:flex;align-items:center;gap:6px;margin:2px 0">
              <span style="display:inline-block;width:10px;height:10px;border-radius:50%;background:${p.color}"></span>
              Orders: <strong>${formatRawNumber(p.value)}</strong>
            </div>
            <div style="display:flex;align-items:center;gap:6px;margin:2px 0">
              <span style="display:inline-block;width:10px;height:10px;border-radius:50%;background:${ECHART_COLORS.accent}"></span>
              Revenue: <strong>Rs ${formatRawNumber(revenue)}</strong>
            </div>`;
        },
      },
      toolbox: ECHART_TOOLBOX,
      grid: { left: "3%", right: "4%", bottom: "3%", top: "15%", containLabel: true },
      xAxis: {
        type: "category",
        data: hours,
        axisLabel: { hideOverlap: false, color: "#64748B", fontSize: 11 },
      },
      yAxis: {
        type: "value",
        name: "Orders",
        nameTextStyle: { color: "#64748B", fontSize: 11 },
        minInterval: 1,
        axisLabel: { formatter: (v) => formatNumber(v), color: "#64748B" },
        splitLine: { lineStyle: { color: "#F1F5F9" } },
      },
      series: [
        {
          name: "Orders",
          type: "bar",
          data: counts.map((c, idx) => ({
            value: c,
            itemStyle: {
              color: idx === peakHour
                ? {
                    type: "linear",
                    x: 0, y: 0, x2: 0, y2: 1,
                    colorStops: [
                      { offset: 0, color: ECHART_COLORS.accentLight },
                      { offset: 1, color: ECHART_COLORS.accent }
                    ]
                  }
                : {
                    type: "linear",
                    x: 0, y: 0, x2: 0, y2: 1,
                    colorStops: [
                      { offset: 0, color: ECHART_COLORS.primaryLight },
                      { offset: 1, color: "rgba(59,130,246,0.3)" }
                    ]
                  },
              borderRadius: [3, 3, 0, 0]
            }
          })),
          barWidth: "60%",
          markPoint: peakCount > 0 ? {
            symbol: "pin",
            symbolSize: 50,
            itemStyle: { color: ECHART_COLORS.accent },
            label: {
              formatter: "Peak",
              color: "#fff",
              fontSize: 10,
              fontWeight: 600
            },
            data: [{ name: "Peak", coord: [peakLabel, peakCount], value: peakCount }],
          } : undefined,
          markLine: peakCount > 0 ? {
            symbol: "none",
            lineStyle: { type: "dashed", color: ECHART_COLORS.accent, width: 1.5 },
            label: {
              formatter: `Peak: ${peakLabel} (${formatNumber(peakCount)} orders)`,
              position: "insideEndTop",
              color: ECHART_COLORS.accent,
              fontSize: 11,
              fontWeight: 600
            },
            data: [{ xAxis: peakLabel }]
          } : undefined,
          emphasis: { focus: "series" },
          animationDuration: 600,
          animationDelay: (idx) => idx * 30,
        },
      ],
    };
  }, [hours, counts, revenues, peakHour, peakCount, hasData]);

  if (!hasData) {
    return (
      <div className="d-flex align-items-center justify-content-center h-100 text-muted">
        No data for the selected period
      </div>
    );
  }

  return <EChart option={option} />;
};

export default EChartsOrdersByHour;
